import theme, { colors } from '@globals/themes/defaultTheme'
import { tabletQuery } from '@globals/themes/themeConstants'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import { ReactNode, useRef } from 'react'
import { FieldValues, FormProvider, UseFormReturn } from 'react-hook-form'
import DirtyFormBlocker from './DirtyFormBlocker'
import FormActionTray from './FormActionTray'
import FormDrawerActionTray from './FormDrawerActionTray'
import FormErrors from './FormErrors'

const dialogActionsStyle: SxProps = {
  position: 'absolute',
  bottom: 0,
  width: '100%',
  mx: -3,
  backgroundColor: colors.white,
  borderTop: `1px solid ${colors.grayExtraLight}`,
  px: 3,
  py: 2,
  [tabletQuery]: {
    position: 'fixed',
  },
}

const dialogContentStyle = (hasError: boolean): SxProps => ({
  pb: 10,
  [tabletQuery]: {
    pb: hasError ? 28 : 18,
  },
})

type FormContextWrapperProps<T extends FieldValues> = {
  formMethods: UseFormReturn<T>
  onSubmit: (data: T) => Promise<unknown>
  unsavedDialogKey?: string
  onRegisterCloseHandler?: (attemptClose: (doClose: () => void) => boolean) => void
  /** When false, the unsaved dialog will only open when the registered close handler is called (e.g. modal close), not on navigation. Default true. */
  enableNavBlocker?: boolean
  children: ReactNode
  formActions?: ReactNode
  formDrawerActions?: ReactNode
  isInDialog?: boolean
  formId?: string
}

const FormContextWrapper = <T extends FieldValues>({
  formMethods,
  onSubmit,
  unsavedDialogKey,
  onRegisterCloseHandler,
  enableNavBlocker = true,
  children,
  formActions,
  formDrawerActions,
  isInDialog = false,
  formId,
}: FormContextWrapperProps<T>) => {
  const { handleSubmit, formState } = formMethods
  const formRef = useRef<HTMLFormElement>(null)
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.stopPropagation() // NOTE: This is to prevent event bubbling to parent forms
    handleSubmit(onSubmit)(e)
  }
  const FormRootErrors = () =>
    formState.errors.root?.message && (
      <Box alignSelf="flex-start" flexGrow={1}>
        <FormErrors errors={formState.errors} />
      </Box>
    )
  return (
    <FormProvider {...formMethods}>
      <form ref={formRef} id={formId} onSubmit={handleFormSubmit} style={{ width: '100%' }}>
        <Box sx={isInDialog ? dialogContentStyle(!!formState.errors.root?.message) : undefined}>
          {children}
        </Box>
        <Box
          sx={isInDialog ? { ...dialogActionsStyle, zIndex: theme.zIndex.modal + 1 } : undefined}>
          {!!formActions && (
            <FormActionTray mt={isInDialog ? 0 : undefined}>
              <FormRootErrors />
              {formActions}
            </FormActionTray>
          )}
          {!!formDrawerActions && (
            <FormDrawerActionTray errors={<FormRootErrors />}>
              {formDrawerActions}
            </FormDrawerActionTray>
          )}
          {!formDrawerActions && !formActions && (
            <Stack>
              <FormRootErrors />
            </Stack>
          )}
        </Box>
      </form>
      {!!unsavedDialogKey && (
        <DirtyFormBlocker
          isFormDirty={formMethods.formState.isDirty}
          dialogKey={unsavedDialogKey}
          isSubmitSuccessful={formMethods.formState.isSubmitSuccessful}
          onRequestClose={onRegisterCloseHandler}
          blockNavigation={enableNavBlocker}
        />
      )}
    </FormProvider>
  )
}

export default FormContextWrapper
